import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface TextDropItem {
  id: string;
  text: string;
  analysis_request?: any;
  analysis_response?: {
    type: string;
    sentiment: string;
    keywords: string[];
    suggestedVoice: string;
    formattedText?: string;
  };
  audio_url?: string;
  voice_id?: string;
  voice_name?: string;
  character_count?: number;
  duration_seconds?: number;
  created_at?: string;
}

export default function TextDrop() {
  const { t } = useTranslation();
  const [droppedText, setDroppedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<string | null>(null);
  const [library, setLibrary] = useState<TextDropItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPasteReady, setIsPasteReady] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [totalCharacters, setTotalCharacters] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [apiLogs, setApiLogs] = useState<Array<{timestamp: number, type: string, data: any}>>([]);

  // Load library from API on mount
  useEffect(() => {
    loadClips();
  }, []);

  const loadClips = async () => {
    try {
      const response = await fetch('/api/audio/clips?limit=50');
      if (response.ok) {
        const data = await response.json();
        setLibrary(data.clips || []);
        // Calculate total characters
        const total = (data.clips || []).reduce((sum: number, item: TextDropItem) =>
          sum + (item.character_count || 0), 0
        );
        setTotalCharacters(total);
      }
    } catch (error) {
      console.error('Failed to load clips:', error);
    }
  };

  // Handle paste from clipboard
  const handlePaste = async () => {
    setError(null);
    try {
      const clipboardItems = await navigator.clipboard.read();

      for (const item of clipboardItems) {
        // Check if clipboard contains text
        if (item.types.includes('text/plain')) {
          const blob = await item.getType('text/plain');
          const text = await blob.text();

          if (text.trim()) {
            setDroppedText(text.trim());
            processText(text.trim());
          } else {
            setError('Clipboard is empty');
          }
        } else {
          setError('Clipboard does not contain text. Please copy some text first.');
        }
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      setError('Failed to access clipboard. Please grant permission and try again.');
    }
  };

  // Process the text through AI analysis and generate audio
  const processText = async (text: string) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Analyze text with Cloudflare AI
      const analysisRequest = { text };
      setApiLogs(prev => [...prev, { timestamp: Date.now(), type: 'analyze-request', data: analysisRequest }]);

      const analysisResponse = await fetch('/api/audio/analyze-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analysisRequest)
      });

      if (!analysisResponse.ok) {
        throw new Error('Failed to analyze text');
      }

      const analysis = await analysisResponse.json();
      setApiLogs(prev => [...prev, { timestamp: Date.now(), type: 'analyze-response', data: analysis }]);

      // Step 2: Generate audio with ElevenLabs
      const audioRequest = {
        text: analysis.formattedText || text,
        voice: analysis.suggestedVoice || 'rachel'
      };
      setApiLogs(prev => [...prev, { timestamp: Date.now(), type: 'audio-request', data: audioRequest }]);

      const audioRequestWithAnalysis = {
        ...audioRequest,
        analysisRequest: analysisRequest,
        analysisResponse: analysis
      };

      const audioResponse = await fetch('/api/audio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(audioRequestWithAnalysis)
      });

      if (!audioResponse.ok) {
        throw new Error('Failed to generate audio');
      }

      const audioData = await audioResponse.json();
      setApiLogs(prev => [...prev, {
        timestamp: Date.now(),
        type: 'audio-response',
        data: audioData
      }]);

      // Reload clips from API
      await loadClips();

      // Play audio automatically
      const audio = new Audio(audioData.audio_url);
      audio.play();

    } catch (err) {
      console.error('Processing error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process text');
    } finally {
      setIsProcessing(false);
    }
  };

  // Play or pause audio from library
  const toggleAudio = (audioUrl: string, itemId: string) => {
    // If this audio is already playing, pause it (keep reference for resume)
    if (playingId === itemId && audioElement) {
      if (audioElement.paused) {
        // Resume playback
        audioElement.play();
      } else {
        // Pause playback
        audioElement.pause();
      }
      return;
    }

    // Stop any currently playing audio
    if (audioElement) {
      audioElement.pause();
      setAudioElement(null);
    }

    // Play new audio
    setCurrentAudio(audioUrl);
    const audio = new Audio(audioUrl);

    audio.addEventListener('ended', () => {
      setPlayingId(null);
      setAudioElement(null);
    });

    audio.play();
    setPlayingId(itemId);
    setAudioElement(audio);
  };

  // Delete item from library
  const deleteItem = async (id: string) => {
    try {
      const response = await fetch(`/api/audio/clips/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Reload clips from API
        await loadClips();
      }
    } catch (error) {
      console.error('Failed to delete clip:', error);
    }
  };

  // Clear all library items
  const clearLibrary = async () => {
    if (confirm('Are you sure you want to clear all audio clips?')) {
      // Delete all clips one by one
      for (const clip of library) {
        await deleteItem(clip.id);
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Credits Display - Top Panel */}
      <div className="relative bg-gradient-to-b from-gray-900 to-black p-1 rounded-sm shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 rounded-sm"></div>

        {/* Side rivets */}
        <div className="absolute top-1/2 -translate-y-1/2 left-2 w-2 h-2 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
        <div className="absolute top-1/2 -translate-y-1/2 right-2 w-2 h-2 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>

        <div className="relative bg-gradient-to-b from-gray-950 via-black to-gray-900 p-4 m-2 border border-gray-700 shadow-inner">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="text-xs font-mono uppercase tracking-wider text-gray-500">
                Credits Used
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-green-400 font-mono" style={{
                  textShadow: '0 0 20px rgba(16,185,129,0.6)'
                }}>
                  {totalCharacters.toLocaleString()}
                </span>
                <span className="text-sm text-gray-500 font-mono uppercase">chars</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">
                Estimated Cost
              </div>
              <div className="text-xl font-bold text-emerald-400 font-mono" style={{
                textShadow: '0 0 15px rgba(16,185,129,0.5)'
              }}>
                ${(totalCharacters * 0.00003).toFixed(4)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Drop Zone - Tactical Panel */}
      <div className="relative bg-gradient-to-b from-gray-900 to-black p-1 rounded-sm shadow-2xl">
        {/* Outer metal frame */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 rounded-sm"></div>

        {/* Corner rivets */}
        <div className="absolute top-2 left-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
        <div className="absolute top-2 right-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
        <div className="absolute bottom-2 left-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
        <div className="absolute bottom-2 right-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>

        {/* Inner panel */}
        <div className="relative bg-gradient-to-b from-gray-950 via-black to-gray-900 p-8 m-2 border border-gray-700 shadow-inner">
          <div className="text-center space-y-6">
            {/* Status indicators */}
            <div className="flex justify-center gap-3 mb-2">
              <div className={`w-3 h-3 rounded-full ${isProcessing ? 'bg-green-500 animate-pulse shadow-lg shadow-green-500/50' : 'bg-gray-700 shadow-inner'}`}></div>
              <div className={`w-3 h-3 rounded-full ${droppedText ? 'bg-green-500 shadow-lg shadow-green-500/50' : 'bg-gray-700 shadow-inner'}`}></div>
              <div className={`w-3 h-3 rounded-full ${error ? 'bg-red-500 shadow-lg shadow-red-500/50' : 'bg-gray-700 shadow-inner'}`}></div>
            </div>

            <div className="text-5xl mb-4 opacity-80">📋🎤</div>

            <h2 className="text-2xl font-bold text-gray-300 tracking-wider uppercase" style={{
              textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 -1px 1px rgba(255,255,255,0.1)'
            }}>
              Text to Audio Drop
            </h2>

            <p className="text-gray-500 uppercase text-sm tracking-wide font-mono">
              Copy any text, then initiate audio synthesis
            </p>

            {/* Main action button - embossed style */}
            <button
              onClick={handlePaste}
              disabled={isProcessing}
              className="relative px-10 py-5 bg-gradient-to-b from-gray-800 to-gray-900 text-green-400 font-bold text-lg uppercase tracking-wider border-2 border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all group overflow-hidden"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.5)',
                textShadow: '0 0 10px rgba(16,185,129,0.5), 0 1px 2px rgba(0,0,0,0.8)'
              }}
            >
              {/* Button highlight */}
              <div className="absolute inset-0 bg-gradient-to-b from-green-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>

              <span className="relative flex items-center justify-center gap-3">
                {isProcessing ? (
                  <>
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <span>📋</span>
                    Paste & Generate Audio
                  </>
                )}
              </span>
            </button>

            {error && (
              <div className="mt-4 p-4 bg-gradient-to-b from-red-950/50 to-black border-2 border-red-800/50 shadow-inner">
                <p className="text-red-400 font-mono text-sm uppercase tracking-wide">{error}</p>
              </div>
            )}

            {droppedText && !isProcessing && (
              <div className="mt-6 p-4 bg-gradient-to-b from-gray-900 to-black border border-gray-700 shadow-inner text-left">
                <p className="text-xs text-green-500 mb-2 uppercase tracking-wider font-mono">Current Input:</p>
                <p className="text-gray-400 line-clamp-3 font-mono text-sm">{droppedText}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Library - Tactical Panel */}
      {library.length > 0 && (
        <div className="relative bg-gradient-to-b from-gray-900 to-black p-1 rounded-sm shadow-2xl">
          {/* Outer metal frame */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 rounded-sm"></div>

          {/* Corner rivets */}
          <div className="absolute top-2 left-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
          <div className="absolute top-2 right-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
          <div className="absolute bottom-2 left-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
          <div className="absolute bottom-2 right-2 w-3 h-3 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>

          {/* Inner panel */}
          <div className="relative bg-gradient-to-b from-gray-950 via-black to-gray-900 p-6 m-2 border border-gray-700 shadow-inner">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-300 uppercase tracking-wider" style={{
                textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 -1px 1px rgba(255,255,255,0.1)'
              }}>
                Audio Library
                <span className="ml-2 text-green-500 font-mono">({library.length})</span>
              </h3>
              <button
                onClick={clearLibrary}
                className="px-4 py-2 text-xs bg-gradient-to-b from-gray-800 to-gray-900 text-red-400 border-2 border-red-800/50 uppercase tracking-wider font-bold hover:border-red-700 transition-colors"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 4px rgba(0,0,0,0.5)',
                  textShadow: '0 0 8px rgba(239,68,68,0.5)'
                }}
              >
                Clear All
              </button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900">
              {library.map((item) => (
                <div
                  key={item.id}
                  className="p-4 bg-gradient-to-b from-gray-900 to-black border border-gray-700 hover:border-green-500/40 transition-colors shadow-inner"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-2 font-mono uppercase tracking-wide">
                        {item.created_at ? new Date(item.created_at).toLocaleString() : 'Unknown'}
                      </p>
                      <p className="text-gray-400 line-clamp-2 mb-3 font-mono text-sm">
                        {item.text}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {item.analysis_response?.type && (
                          <span className="text-xs px-2 py-1 bg-gradient-to-b from-gray-800 to-gray-900 text-gray-400 border border-gray-700 font-mono uppercase tracking-wider shadow-inner">
                            {item.analysis_response.type}
                          </span>
                        )}
                        {item.analysis_response?.sentiment && (
                          <span className="text-xs px-2 py-1 bg-gradient-to-b from-green-950 to-black text-green-400 border border-green-800/50 font-mono uppercase tracking-wider shadow-inner">
                            {item.analysis_response.sentiment}
                          </span>
                        )}
                        {item.voice_name && (
                          <span className="text-xs px-2 py-1 bg-gradient-to-b from-emerald-950 to-black text-emerald-400 border border-emerald-800/50 font-mono uppercase tracking-wider shadow-inner">
                            {item.voice_name}
                          </span>
                        )}
                        {item.character_count && (
                          <span className="text-xs px-2 py-1 bg-gradient-to-b from-blue-950 to-black text-blue-400 border border-blue-800/50 font-mono uppercase tracking-wider shadow-inner">
                            {item.character_count} chars
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {item.audio_url && (
                        <button
                          onClick={() => toggleAudio(item.audio_url!, item.id)}
                          className={`p-3 bg-gradient-to-b border-2 transition-colors group ${
                            playingId === item.id && audioElement && !audioElement.paused
                              ? 'from-yellow-600 to-yellow-800 border-yellow-500'
                              : 'from-green-700 to-green-900 border-green-600 hover:from-green-600 hover:to-green-800'
                          }`}
                          style={{
                            boxShadow: playingId === item.id && audioElement && !audioElement.paused
                              ? 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -2px 4px rgba(0,0,0,0.5), 0 0 15px rgba(234,179,8,0.5)'
                              : 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -2px 4px rgba(0,0,0,0.5), 0 0 15px rgba(16,185,129,0.3)'
                          }}
                          title={playingId === item.id && audioElement && !audioElement.paused ? "Pause audio" : "Play audio"}
                        >
                          {playingId === item.id && audioElement && !audioElement.paused ? '⏸️' : '▶️'}
                        </button>
                      )}
                      <button
                        onClick={() => deleteItem(item.id)}
                        className="p-3 bg-gradient-to-b from-gray-800 to-gray-900 text-red-400 border-2 border-red-800/50 hover:border-red-700 transition-colors"
                        style={{
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 4px rgba(0,0,0,0.5)'
                        }}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Info Section - Tactical Panel */}
      <div className="relative bg-gradient-to-b from-gray-900 to-black p-1 rounded-sm shadow-2xl">
        {/* Outer metal frame */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 rounded-sm"></div>

        {/* Side rivets */}
        <div className="absolute top-1/2 -translate-y-1/2 left-2 w-2 h-2 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
        <div className="absolute top-1/2 -translate-y-1/2 right-2 w-2 h-2 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>

        {/* Inner panel */}
        <div className="relative bg-gradient-to-b from-gray-950 via-black to-gray-900 p-4 m-2 border border-gray-700 shadow-inner">
          <p className="text-gray-500 text-xs font-mono uppercase tracking-wide leading-relaxed">
            <strong className="text-green-500">SYSTEM INFO:</strong> Copy text → Initiate synthesis → AI analysis → Format optimization → ElevenLabs audio generation. All clips stored locally.
          </p>
        </div>
      </div>

      {/* Debug Panel */}
      <div className="relative bg-gradient-to-b from-gray-900 to-black p-1 rounded-sm shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 rounded-sm"></div>

        {/* Corner rivets */}
        <div className="absolute top-2 left-2 w-2 h-2 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>
        <div className="absolute top-2 right-2 w-2 h-2 bg-gray-600 rounded-full shadow-inner border border-gray-500"></div>

        <div className="relative bg-gradient-to-b from-gray-950 via-black to-gray-900 p-4 m-2 border border-gray-700 shadow-inner">
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="w-full flex justify-between items-center px-4 py-2 bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-gray-700 hover:border-green-500/40 transition-colors"
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 4px rgba(0,0,0,0.5)'
            }}
          >
            <span className="text-sm font-mono uppercase tracking-wider text-green-400" style={{
              textShadow: '0 0 10px rgba(16,185,129,0.5)'
            }}>
              API Debug Console {apiLogs.length > 0 && `(${apiLogs.length})`}
            </span>
            <span className="text-green-400">{showDebug ? '▼' : '▶'}</span>
          </button>

          {showDebug && (
            <div className="mt-4 space-y-2 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900">
              {apiLogs.length === 0 ? (
                <p className="text-gray-600 font-mono text-xs text-center py-4">No API calls yet</p>
              ) : (
                apiLogs.map((log, idx) => (
                  <div key={idx} className="p-3 bg-gradient-to-b from-gray-900 to-black border border-gray-700 shadow-inner">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`text-xs font-mono uppercase tracking-wider ${
                        log.type.includes('request') ? 'text-blue-400' : 'text-green-400'
                      }`}>
                        {log.type}
                      </span>
                      <span className="text-xs text-gray-600 font-mono">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <pre className="text-xs font-mono text-gray-400 overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  </div>
                ))
              )}
              {apiLogs.length > 0 && (
                <button
                  onClick={() => setApiLogs([])}
                  className="w-full mt-2 px-4 py-2 text-xs bg-gradient-to-b from-gray-800 to-gray-900 text-red-400 border-2 border-red-800/50 uppercase tracking-wider font-bold hover:border-red-700 transition-colors"
                  style={{
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 4px rgba(0,0,0,0.5)'
                  }}
                >
                  Clear Logs
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
